import React, { useState, useMemo } from 'react';
import { useTranslation } from 'react-i18next';
import { useMediaQuery } from 'react-responsive';

import { useQuery } from '@tanstack/react-query';

import classNames from 'classnames';
import AdminService from '../../../../../../services/AdminService';
import Dropdown from '../../../../../Form/Dropdown';
import Card from '../../../../../common/Card';
import ColumnLayout from '../../../../../common/Layout/ColumnLayout';
import classes from './styles.module.scss';

const sortJobs = (sortOption) => {
  if (!sortOption) {
    return {};
  }

  if (sortOption.value === 'title') {
    return {
      orderBy: 'jobTitle',
      sortOrder: 'desc',
    };
  }
  if (sortOption.value === 'submitted') {
    return {
      orderBy: 'summary.clientSubmitted',
      sortOrder: 'asc',
    };
  }

  if (sortOption.value === 'days') {
    return {
      orderBy: 'createdAt',
      sortOrder: 'asc',
    };
  }

  return {};
};

export default function Details({ company }) {
  const { t } = useTranslation();
  const isTabletOrMobile = useMediaQuery({ query: '(max-width: 1024px)' });

  // State for collapsible sections
  const [expandedSections, setExpandedSections] = useState({
    clientResponse: false,
    recruiterActions: false,
    slaAlerts: false,
  });

  const toggleSection = (sectionKey) => {
    setExpandedSections((prev) => ({
      ...prev,
      [sectionKey]: !prev[sectionKey],
    }));
  };

  const [activeJobSortOption, setActiveJobSortOption] = useState('');
  const [activeCandidateSortOption, setActiveCandidateSortOption] =
    useState('');
  const [activeActivitySortOption, setActiveActivitySortOption] = useState('');

  const jobSortOptions = [
    {
      label: t(
        'dashboardComponents.Companies.CompanyDetails.Details.sort.job.title'
      ),
      value: 'title',
    },
    {
      label: t(
        'dashboardComponents.Companies.CompanyDetails.Details.sort.job.submitted'
      ),
      value: 'submitted',
    },
    {
      label: t(
        'dashboardComponents.Companies.CompanyDetails.Details.sort.job.days'
      ),
      value: 'days',
    },
  ];

  function getDate(date) {
    return `${date.getDate()}th ${date.toLocaleString('en-us', {
      month: 'short',
    })} ${date.getFullYear()}`;
  }

  const { data: jobPages } = useQuery({
    queryKey: ['companyJobs', activeJobSortOption, company],
    queryFn: () => {
      const filters = ['status:noteq:Draft'];
      filters.push(`company.id:eq:${company?.id}`);

      return AdminService.getCompanyJobs({
        pageNumber: 1,
        pageSize: 9999,
        ...sortJobs(activeJobSortOption),
        Filters: filters,
      });
    },
    enabled: !!company,
    keepPreviousData: true,
  });

  const { data: jobApplications } = useQuery({
    queryKey: ['companyJobApplications', activeCandidateSortOption, company],
    queryFn: () => {
      const filters = [];
      if (activeCandidateSortOption?.value) {
        filters.push(
          `jobApplication.jobPostId:eq:${activeCandidateSortOption.value}`
        );
      }

      return AdminService.getCompanyJobApplications({
        pageNumber: 1,
        pageSize: 9999,
        companyId: company?.id,
        Filters: filters,
      });
    },
    enabled: !!company,
    keepPreviousData: false,
  });

  const jobs = jobPages?.data;

  const submittedCandidates = jobApplications?.data.filter(
    (candidate) => candidate.status === 'ClientSubmit'
  );

  const candidateSortOptions = [
    {
      label: t('common.All', 'All'),
      value: '',
      onClick: () => setActiveCandidateSortOption(''),
    },
    ...(jobs?.map((job) => ({
      label: job.jobTitle,
      value: job.id,
    })) ?? []),
  ];

  const pipelineStages = useMemo(() => {
    const apps = jobApplications?.data ?? [];
    const countBy = (status) =>
      apps.reduce((acc, curr) => acc + (curr.status === status ? 1 : 0), 0);

    return {
      applied: {
        key: 'applied',
        count: countBy('New'),
      },
      // screen: {
      //   key: 'screen',
      //   count: countBy('Screen'),
      // },
      interview: {
        key: 'interview',
        count: countBy('Interview'),
      },
      clientSubmit: {
        key: 'clientSubmit',
        count: countBy('ClientSubmit'),
      },
      clientInterview: {
        key: 'clientInterview',
        count: countBy('ClientInterview'),
        highlighted: true,
      },
      offer: {
        key: 'offer',
        count: countBy('Offer'),
      },
      onboard: {
        key: 'onboard',
        count: countBy('Onboard'),
      },
    };
  }, [jobApplications?.data]);

  const totalCount = useMemo(() => {
    return (
      pipelineStages?.applied?.count +
      pipelineStages?.interview?.count +
      pipelineStages?.clientSubmit?.count +
      pipelineStages?.clientInterview?.count +
      pipelineStages?.offer?.count +
      pipelineStages?.onboard?.count
    );
  }, [pipelineStages]);

  const recruiterActions = [];

  const slaAlerts = [];

  return (
    <div className={classes.Details}>
      <ColumnLayout className={classes.Layout}>
        <ColumnLayout.Column>
          <Card>
            <Card.Header>
              <div className={classes.SectionHeader}>
                <div className={classes.HeaderLeft}>
                  <h3 className={classes.SectionTitle}>
                    {t('details.openJobs', 'Open Jobs')}
                  </h3>
                  <span
                    className={classNames(
                      classes.CountBadge,
                      jobs?.length === 0 ? classes.empty : ''
                    )}
                  >
                    {jobs?.length ?? 0}
                  </span>
                </div>
                <div className={classes.HeaderRight}>
                  <Dropdown
                    options={jobSortOptions}
                    placeholder={t('common.sort', 'Sort')}
                    activeOption={activeJobSortOption}
                    setActiveOption={setActiveJobSortOption}
                    width={isTabletOrMobile ? 139 : 180}
                    height={45}
                    style={{
                      borderRadius: '16px',
                      border: '1px solid #F2F4F8',
                      background: '#FFF',
                      boxShadow: '0 -4px 60px 0 rgba(62, 63, 99, 0.10)',
                    }}
                  />
                </div>
              </div>
            </Card.Header>
            <Card.Body scrollable className={classes.OpenJobsContent}>
              {jobs?.length > 0 ? (
                <>
                  <div className={classes.TableHeader}>
                    <div className={classes.HeaderColumn}>
                      {t('details.jobTitle', 'Job Title')}
                    </div>
                    <div className={classes.HeaderColumn}>
                      {t('details.candidates', 'Submitted')}
                    </div>
                    <div className={classes.HeaderColumn}>
                      {t('details.daysOpen', 'Days Open')}
                    </div>
                  </div>
                  <div className={classes.JobsList}>
                    {jobs?.map((job) => (
                      <div key={job.id} className={classes.JobRow}>
                        <div className={classes.JobTitle}>{job.jobTitle}</div>
                        <div className={classes.CandidateCount}>
                          <span className={classes.DaysBadge}>
                            {job.summary.clientSubmit}
                          </span>
                        </div>
                        <div className={classes.DaysOpen}>
                          <span className={classes.DaysBadge}>
                            {job.daysOpen}
                          </span>
                        </div>
                      </div>
                    ))}
                  </div>
                </>
              ) : (
                <div className={classes.EmptyText}>
                  There are no open jobs for this client at the moment
                </div>
              )}
            </Card.Body>
          </Card>
        </ColumnLayout.Column>

        <ColumnLayout.Column>
          <Card className={classes.CandidateSubmittedCard}>
            <Card.Header>
              <div className={classes.SectionHeader}>
                <div className={classes.HeaderLeft}>
                  <h3 className={classes.SectionTitle}>
                    {t('details.candidateSubmitted', 'Candidate Submitted')}
                  </h3>
                  <span
                    className={classNames(
                      classes.CountBadge,
                      submittedCandidates?.length === 0 ? classes.empty : ''
                    )}
                  >
                    {submittedCandidates?.length ?? 0}
                  </span>
                </div>
                <div className={classes.HeaderRight}>
                  <Dropdown
                    options={candidateSortOptions}
                    placeholder={t('details.selectJob', 'Select Job')}
                    activeOption={activeCandidateSortOption}
                    setActiveOption={setActiveCandidateSortOption}
                    width={isTabletOrMobile ? 139 : 180}
                    height={45}
                    style={{
                      borderRadius: '16px',
                      border: '1px solid #F2F4F8',
                      background: '#FFF',
                      boxShadow: '0 -4px 60px 0 rgba(62, 63, 99, 0.10)',
                    }}
                  />
                </div>
              </div>
            </Card.Header>
            <Card.Body scrollable className={classes.CandidateSubmittedBody}>
              {submittedCandidates?.length > 0 ? (
                <div className={classes.CandidateTableContainer}>
                  <table className={classes.CandidateTable}>
                    <thead>
                      <tr className={classes.TableHeaderRow}>
                        <th className={classes.TableHeaderCell}>
                          {t('common.name', 'Name')}
                        </th>
                        <th className={classes.TableHeaderCell}>
                          {t('common.role', 'Job')}
                        </th>
                        <th className={classes.TableHeaderCell}>
                          {t('details.submitDate', 'Submitted Date')}
                        </th>
                        <th className={classes.TableHeaderCell}>
                          {t('details.stage', 'Stage')}
                        </th>
                      </tr>
                    </thead>
                    <tbody>
                      {submittedCandidates?.map((candidate) => {
                        const {
                          user: { userProfile },
                          jobPost,
                        } = candidate.jobApplication;
                        return (
                          <tr key={candidate.id} className={classes.TableRow}>
                            <td className={classes.TableCell}>
                              <div className={classes.CandidateName}>
                                {userProfile.firstName} {userProfile.lastName}
                              </div>
                            </td>
                            <td className={classes.TableCell}>
                              <div className={classes.CandidateRole}>
                                {jobPost.jobTitle}
                              </div>
                            </td>
                            <td className={classes.TableCell}>
                              <div className={classes.SubmitDate}>
                                {getDate(new Date(candidate.createdAt))}
                              </div>
                            </td>
                            <td className={classes.TableCell}>
                              <div className={classes.Stage}>
                                {candidate.status}
                              </div>
                            </td>
                          </tr>
                        );
                      })}
                    </tbody>
                  </table>
                </div>
              ) : (
                <div className={classes.EmptyText}>
                  No candidates submitted to the job at the moment
                </div>
              )}
            </Card.Body>
          </Card>
          <Card isFixed>
            <Card.Header>
              <div className={classes.PipelineHeader}>
                <h4 className={classes.PipelineTitle}>
                  {t('details.candidatePipeline', 'Candidate Pipeline')}
                </h4>
                <span
                  className={classNames(
                    classes.CountBadge,
                    totalCount === 0 ? classes.empty : ''
                  )}
                >
                  {totalCount ?? 0}
                </span>
              </div>
            </Card.Header>
            <Card.Body scrollable={false}>
              <div className={classes.PipelineStages}>
                <div className={classes.PipelineStage}>
                  <div className={classes.StageCircle}>
                    <span className={classes.StageCount}>
                      {pipelineStages.applied.count}
                    </span>
                  </div>
                  <div className={classes.StageLabel}>
                    {t(`details.pipeline.stages.${pipelineStages.applied.key}`)}
                  </div>
                </div>
                <div className={classes.PipelineStage}>
                  <div className={classes.StageCircle}>
                    <span className={classes.StageCount}>
                      {pipelineStages.interview.count}
                    </span>
                  </div>
                  <div className={classes.StageLabel}>
                    {t(
                      `details.pipeline.stages.${pipelineStages.interview.key}`
                    )}
                  </div>
                </div>
                <div className={classes.ClientHighlight}>
                  <div className={classes.ClientHighlightBackground} />
                  <div className={classes.HighLiteLabel}>
                    {t('details.pipeline.stages.client')}
                  </div>
                  <div className={classes.PipelineStage}>
                    <div className={classes.StageCircle}>
                      <span className={classes.StageCount}>
                        {pipelineStages.clientSubmit.count}
                      </span>
                    </div>
                    <div className={classes.StageLabel}>
                      {t(
                        `details.pipeline.stages.${pipelineStages.clientSubmit.key}`
                      )}
                    </div>
                  </div>
                  <div className={classes.PipelineStage}>
                    <div className={classes.StageCircle}>
                      <span className={classes.StageCount}>
                        {pipelineStages.clientInterview.count}
                      </span>
                    </div>
                    <div className={classes.StageLabel}>
                      {t(
                        `details.pipeline.stages.${pipelineStages.clientInterview.key}`
                      )}
                    </div>
                  </div>
                </div>
                <div className={classes.PipelineStage}>
                  <div className={classes.StageCircle}>
                    <span className={classes.StageCount}>
                      {pipelineStages.offer.count}
                    </span>
                  </div>
                  <div className={classes.StageLabel}>
                    {t(`details.pipeline.stages.${pipelineStages.offer.key}`)}
                  </div>
                </div>
                <div className={classes.PipelineStage}>
                  <div className={classes.StageCircle}>
                    <span className={classes.StageCount}>
                      {pipelineStages.onboard.count}
                    </span>
                  </div>
                  <div className={classes.StageLabel}>
                    {t(`details.pipeline.stages.${pipelineStages.onboard.key}`)}
                  </div>
                </div>
              </div>
            </Card.Body>
          </Card>
        </ColumnLayout.Column>

        <ColumnLayout.Column>
          <Card>
            <Card.Header>
              <div className={classes.SectionHeader}>
                <div className={classes.HeaderLeft}>
                  <h3 className={classes.SectionTitle}>
                    {t('details.outstandingActions', 'Outstanding Actions')}
                  </h3>
                  <span
                    className={classNames(classes.CountBadge, classes.empty)}
                  >
                    0
                  </span>
                </div>
                <div className={classes.HeaderRight}>
                  <Dropdown
                    options={candidateSortOptions}
                    placeholder={t('details.selectJob', 'Select Job')}
                    activeOption={activeActivitySortOption}
                    setActiveOption={setActiveActivitySortOption}
                    width={isTabletOrMobile ? 139 : 180}
                    height={45}
                    style={{
                      borderRadius: '16px',
                      border: '1px solid #F2F4F8',
                      background: '#FFF',
                      boxShadow: '0 -4px 60px 0 rgba(62, 63, 99, 0.10)',
                    }}
                  />
                </div>
              </div>
              <div className={classes.ActionLegend}>
                <div className={classes.LegendItem}>
                  <div className={`${classes.LegendDot} ${classes.Overdue}`} />
                  <span>{t('details.overdue', 'Overdue')}</span>
                </div>
                <div className={classes.LegendItem}>
                  <div className={`${classes.LegendDot} ${classes.DueSoon}`} />
                  <span>{t('details.dueSoon', 'Due Soon')}</span>
                </div>
                <div className={classes.LegendItem}>
                  <div className={`${classes.LegendDot} ${classes.Schedule}`} />
                  <span>{t('details.schedule', 'Schedule')}</span>
                </div>
              </div>
            </Card.Header>

            <Card.Body scrollable className={classes.ActionsContent}>
              <div className={classes.ActionsList}>
                {/* Client Response Pending */}
                <div className={classes.ActionGroup}>
                  <div
                    className={classes.ActionGroupHeader}
                    onClick={() => toggleSection('clientResponse')}
                  >
                    <span className={classes.ActionGroupTitle}>
                      {t(
                        'details.clientResponsePending',
                        'Client Response Pending'
                      )}
                    </span>
                    <div className={classes.HeaderRight}>
                      <span
                        className={classNames(
                          classes.CountBadge,
                          classes.empty
                        )}
                      >
                        0
                      </span>
                    </div>
                  </div>
                  {expandedSections.clientResponse && (
                    <div className={classes.ActionItems}>
                      {recruiterActions.length > 0 ? (
                        recruiterActions.map((action) => (
                          <div key={action.id} className={classes.ActionItem}>
                            <div className={classes.ActionLeft}>
                              <div
                                className={`${classes.ActionDot} ${classes.Overdue}`}
                              />
                              <span className={classes.ActionText}>
                                Waiting for client feedback on submitted
                                candidates
                              </span>
                            </div>
                            <div
                              className={`${classes.ActionBadge} ${classes.OverdueBadge}`}
                            >
                              2 days
                            </div>
                          </div>
                        ))
                      ) : (
                        <div className={classes.ActionItem}>
                          <span className={classes.EmptyText}>
                            No outstanding actions at the moment
                          </span>
                        </div>
                      )}
                    </div>
                  )}
                </div>

                {/* Recruiter Actions */}
                <div className={classes.ActionGroup}>
                  <div
                    className={classes.ActionGroupHeader}
                    onClick={() => toggleSection('recruiterActions')}
                  >
                    <span className={classes.ActionGroupTitle}>
                      {t('details.recruiterActions', 'Recruiter Actions')}
                    </span>
                    <div className={classes.HeaderRight}>
                      <span
                        className={classNames(
                          classes.CountBadge,
                          classes.empty
                        )}
                      >
                        0
                      </span>
                    </div>
                  </div>
                  {expandedSections.recruiterActions && (
                    <div className={classes.ActionItems}>
                      {recruiterActions.length > 0 ? (
                        recruiterActions.map((action) => (
                          <div key={action.id} className={classes.ActionItem}>
                            <div className={classes.ActionLeft}>
                              <div
                                className={`${classes.ActionDot} ${
                                  classes[
                                    action.status === 'overdue'
                                      ? 'Overdue'
                                      : 'DueSoon'
                                  ]
                                }`}
                              />
                              <span className={classes.ActionText}>
                                {action.text}
                              </span>
                            </div>
                            <div
                              className={`${classes.ActionBadge} ${
                                classes[
                                  action.status === 'overdue'
                                    ? 'OverdueBadge'
                                    : 'DueSoonBadge'
                                ]
                              }`}
                            >
                              {action.timeLabel}
                            </div>
                          </div>
                        ))
                      ) : (
                        <div className={classes.ActionItem}>
                          <span className={classes.EmptyText}>
                            No outstanding actions at the moment
                          </span>
                        </div>
                      )}
                    </div>
                  )}
                </div>

                {/* Job / SLA Alerts */}
                <div className={classes.ActionGroup}>
                  <div
                    className={classes.ActionGroupHeader}
                    onClick={() => toggleSection('slaAlerts')}
                  >
                    <span className={classes.ActionGroupTitle}>
                      {t('details.jobSlaAlerts', 'Job / SLA Alerts')}
                    </span>
                    <div className={classes.HeaderRight}>
                      <span
                        className={classNames(
                          classes.CountBadge,
                          classes.empty
                        )}
                      >
                        0
                      </span>
                    </div>
                  </div>
                  {expandedSections.slaAlerts && (
                    <div className={classes.ActionItems}>
                      {slaAlerts.length > 0 ? (
                        slaAlerts.map((alert) => (
                          <div key={alert.id} className={classes.ActionItem}>
                            <div className={classes.ActionLeft}>
                              <div
                                className={`${classes.ActionDot} ${
                                  classes[
                                    alert.status === 'overdue'
                                      ? 'Overdue'
                                      : 'DueSoon'
                                  ]
                                }`}
                              />
                              <span className={classes.ActionText}>
                                {alert.text}
                              </span>
                            </div>
                            <div
                              className={`${classes.ActionBadge} ${
                                classes[
                                  alert.status === 'overdue'
                                    ? 'OverdueBadge'
                                    : 'DueSoonBadge'
                                ]
                              }`}
                            >
                              {alert.timeLabel}
                            </div>
                          </div>
                        ))
                      ) : (
                        <div className={classes.ActionItem}>
                          <span className={classes.EmptyText}>
                            No outstanding actions at the moment
                          </span>
                        </div>
                      )}
                    </div>
                  )}
                </div>
              </div>
            </Card.Body>
          </Card>
        </ColumnLayout.Column>
      </ColumnLayout>
    </div>
  );
}
